import { useState } from "react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@sakura-grp/ui/drawer";
import { Button } from "@sakura-grp/ui/button";
import { Calendar, type DateRange } from "@sakura-grp/ui/calendar";
import { Label } from "@sakura-grp/ui/label";
import { X } from "lucide-react";
import { format, subDays, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { ja } from "date-fns/locale";

const PRESETS = [
  { label: "今日", getValue: () => {
    const today = new Date();
    return { from: today, to: today };
  }},
  { label: "過去7日", getValue: () => {
    const today = new Date();
    return { from: subDays(today, 6), to: today };
  }},
  { label: "今月", getValue: () => {
    const today = new Date();
    return { from: startOfMonth(today), to: today };
  }},
  { label: "先月", getValue: () => {
    const today = new Date();
    return { from: startOfMonth(subMonths(today, 1)), to: endOfMonth(subMonths(today, 1)) };
  }},
];

interface AnnouncementsFilterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dateRange: DateRange | undefined;
  onDateRangeChange: (range: DateRange | undefined) => void;
}

export function AnnouncementsFilterDialog({
  open,
  onOpenChange,
  dateRange,
  onDateRangeChange,
}: AnnouncementsFilterDialogProps) {
  const [localDateRange, setLocalDateRange] = useState<DateRange | undefined>(dateRange);

  // Dialog が開いたときに親の状態を同期
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      setLocalDateRange(dateRange);
    }
    onOpenChange(newOpen);
  };

  const handleApply = () => {
    onDateRangeChange(localDateRange);
    onOpenChange(false);
  };

  const handleReset = () => {
    setLocalDateRange(undefined);
    onDateRangeChange(undefined);
  };

  const clearDateRange = () => {
    setLocalDateRange(undefined);
  };

  const handleDateSelect = (range: DateRange | undefined) => {
    setLocalDateRange(range);
  };

  const handlePresetClick = (preset: typeof PRESETS[number]) => {
    const range = preset.getValue();
    setLocalDateRange(range);
  };

  const formatDateRange = () => {
    if (!localDateRange?.from) return "未選択";
    if (!localDateRange.to) return format(localDateRange.from, "yyyy/MM/dd", { locale: ja });
    return `${format(localDateRange.from, "yyyy/MM/dd", { locale: ja })} ~ ${format(localDateRange.to, "yyyy/MM/dd", { locale: ja })}`;
  };

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerContent className="flex flex-col p-0">
        {/* Sticky Header */}
        <DrawerHeader className="sticky top-0 bg-background z-10 px-4 pt-4 pb-2 border-b">
          <div className="flex items-center justify-between">
            <DrawerTitle className="text-lg font-bold">絞込</DrawerTitle>
          </div>
        </DrawerHeader>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6">
          {/* 報告日範囲 */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-bold">報告日</Label>
              {localDateRange?.from && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearDateRange}
                  className="h-8 px-2 text-muted-foreground"
                >
                  <X className="h-4 w-4 mr-1" />
                  クリア
                </Button>
              )}
            </div>

            {/* 選択中の日付表示 */}
            <div className="text-sm text-muted-foreground bg-muted/50 rounded-lg px-3 py-2">
              {formatDateRange()}
            </div>

            {/* プリセットボタン - タッチフレンドリー */}
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  variant="outline"
                  className="h-11 px-4 text-sm"
                  onClick={() => handlePresetClick(preset)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>

            {/* カレンダー - 直接表示 */}
            <div className="flex justify-center border rounded-lg p-2 bg-background">
              <Calendar
                mode="range"
                selected={localDateRange}
                onSelect={handleDateSelect}
                numberOfMonths={1}
                locale={ja}
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Sticky Footer */}
        <DrawerFooter className="sticky bottom-0 bg-background border-t px-4 py-4 gap-3 flex-row">
          <Button
            variant="outline"
            onClick={handleReset}
            className="flex-1 h-12 text-base"
          >
            リセット
          </Button>
          <Button
            onClick={handleApply}
            className="flex-1 h-12 text-base"
          >
            適用
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
